/**
 * Auth Crypto
 *
 * AES-256-GCM encryption/decryption for JWT tokens.
 * Uses AUTH_SECRET env var as the encryption key.
 *
 * The encrypted payload is stored in an httpOnly cookie,
 * making it impossible for client-side JavaScript to read the JWT.
 */

import { createCipheriv, createDecipheriv, randomBytes, scryptSync } from 'crypto';
import type { EncryptedTokenPayload } from './types';

const ALGORITHM = 'aes-256-gcm';
const IV_LENGTH = 16;
const AUTH_TAG_LENGTH = 16;
const SALT = 'gallet-auth-salt'; // Static salt - key derivation happens once per secret

/**
 * Get the encryption key from AUTH_SECRET env var.
 * Uses scrypt to derive a 32-byte key from the secret.
 */
function getEncryptionKey(): Buffer {
  const secret = process.env.AUTH_SECRET;

  if (!secret) {
    throw new Error('AUTH_SECRET environment variable is required for authentication');
  }

  if (secret.length < 32) {
    throw new Error('AUTH_SECRET must be at least 32 characters');
  }

  // Derive a 32-byte key from the secret using scrypt
  return scryptSync(secret, SALT, 32);
}

/**
 * Encrypt a token payload for storage in a cookie.
 *
 * @param payload - The tokens to encrypt
 * @returns Base64-encoded encrypted string (iv + authTag + ciphertext)
 */
export function encryptTokens(payload: EncryptedTokenPayload): string {
  const key = getEncryptionKey();
  const iv = randomBytes(IV_LENGTH);
  const cipher = createCipheriv(ALGORITHM, key, iv);

  const plaintext = JSON.stringify(payload);
  const encrypted = Buffer.concat([cipher.update(plaintext, 'utf8'), cipher.final()]);

  const authTag = cipher.getAuthTag();

  // Combine iv + authTag + encrypted into single buffer
  const combined = Buffer.concat([iv, authTag, encrypted]);

  return combined.toString('base64');
}

/**
 * Decrypt a token payload from a cookie.
 *
 * @param encryptedData - Base64-encoded encrypted string
 * @returns The decrypted token payload, or null if decryption fails
 */
export function decryptTokens(encryptedData: string): EncryptedTokenPayload | null {
  try {
    const key = getEncryptionKey();
    const combined = Buffer.from(encryptedData, 'base64');

    // Extract iv, authTag, and encrypted data
    const iv = combined.subarray(0, IV_LENGTH);
    const authTag = combined.subarray(IV_LENGTH, IV_LENGTH + AUTH_TAG_LENGTH);
    const encrypted = combined.subarray(IV_LENGTH + AUTH_TAG_LENGTH);

    const decipher = createDecipheriv(ALGORITHM, key, iv);
    decipher.setAuthTag(authTag);

    const decrypted = Buffer.concat([decipher.update(encrypted), decipher.final()]);

    return JSON.parse(decrypted.toString('utf8')) as EncryptedTokenPayload;
  } catch {
    // Decryption failed - invalid data, wrong key, or tampered
    return null;
  }
}
